import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Box, IconButton, Menu, MenuItem, Divider } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { useAuth } from "../hooks/useAuth";

const AppHeader: React.FC = () => {
  const { sessionStatus, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] =
    useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);
  const mobileMenuOpen = Boolean(mobileMenuAnchorEl);

  const isSignedIn = sessionStatus !== "signed_out";
  const hasProfile = sessionStatus === "signed_in_with_profile";

  const handleMenu = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);
  const handleMobileMenu = (event: React.MouseEvent<HTMLElement>) =>
    setMobileMenuAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleMobileMenuClose = () => setMobileMenuAnchorEl(null);

  const handleNavigation = (path: string) => {
    handleClose();
    handleMobileMenuClose();
    navigate(path);
  };

  const handleSignOut = async () => {
    handleClose();
    handleMobileMenuClose();
    await signOut();
    navigate("/");
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <header
      className="container mx-auto px-4 py-6"
      style={{ paddingTop: "0.5rem", paddingBottom: "0.5rem" }}
    >
      <div className="flex items-center justify-between">
        <div
          className="text-xl font-light tracking-wider cursor-pointer"
          onClick={() => navigate(hasProfile ? "/vote" : "/")}
        >
          crackd
        </div>

        {hasProfile && (
          <nav className="hidden md:flex space-x-6 text-sm font-light text-[#333333]">
            <Link
              to="/about"
              className={`transition-opacity hover:opacity-50 tracking-wider ${
                isActive("/about") ? "font-medium" : ""
              }`}
            >
              about
            </Link>
            <Link
              to="/vote"
              className={`transition-opacity hover:opacity-50 tracking-wider ${
                isActive("/vote") ? "font-medium" : ""
              }`}
            >
              vote
            </Link>
            <Link
              to="/rankings"
              className={`transition-opacity hover:opacity-50 tracking-wider ${
                isActive("/rankings") ? "font-medium" : ""
              }`}
            >
              leaderboard
            </Link>
          </nav>
        )}

        {isSignedIn && (
          <>
            {/* Mobile Menu */}
            <Box className="md:hidden">
              <IconButton
                size="small"
                aria-label="menu"
                aria-controls="mobile-menu"
                aria-haspopup="true"
                onClick={handleMobileMenu}
                className="text-[#333333] transition-opacity hover:opacity-70"
                sx={{
                  border: "1px solid rgba(51, 51, 51, 0.2)",
                  borderRadius: "0.375rem",
                  padding: "0.375rem",
                  "&:hover": {
                    border: "1px solid rgba(51, 51, 51, 0.6)",
                    background: "transparent",
                  },
                }}
              >
                <MenuIcon fontSize="small" />
              </IconButton>
              <Menu
                id="mobile-menu"
                anchorEl={mobileMenuAnchorEl}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                open={mobileMenuOpen}
                onClose={handleMobileMenuClose}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    border: "1px solid rgba(51, 51, 51, 0.2)",
                    borderRadius: "0.375rem",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)",
                  },
                }}
              >
                {hasProfile && (
                  <MenuItem
                    onClick={() => handleNavigation("/vote")}
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 300,
                      letterSpacing: "0.05em",
                      color: "#333333",
                      transition: "opacity 0.2s",
                      "&:hover": {
                        opacity: 0.7,
                        background: "rgba(51, 51, 51, 0.04)",
                      },
                    }}
                  >
                    Vote
                  </MenuItem>
                )}
                {hasProfile && (
                  <MenuItem
                    onClick={() => handleNavigation("/rankings")}
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 300,
                      letterSpacing: "0.05em",
                      color: "#333333",
                      transition: "opacity 0.2s",
                      "&:hover": {
                        opacity: 0.7,
                        background: "rgba(51, 51, 51, 0.04)",
                      },
                    }}
                  >
                    Leaderboard
                  </MenuItem>
                )}
                {hasProfile && (
                  <MenuItem
                    onClick={() => handleNavigation("/about")}
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 300,
                      letterSpacing: "0.05em",
                      color: "#333333",
                      transition: "opacity 0.2s",
                      "&:hover": {
                        opacity: 0.7,
                        background: "rgba(51, 51, 51, 0.04)",
                      },
                    }}
                  >
                    About
                  </MenuItem>
                )}
                {hasProfile && (
                  <Divider
                    sx={{
                      margin: "0.5rem 0",
                      backgroundColor: "rgba(51, 51, 51, 0.1)",
                    }}
                  />
                )}
                {hasProfile && (
                  <MenuItem
                    onClick={() => handleNavigation("/profile")}
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 300,
                      letterSpacing: "0.05em",
                      color: "#333333",
                      transition: "opacity 0.2s",
                      "&:hover": {
                        opacity: 0.7,
                        background: "rgba(51, 51, 51, 0.04)",
                      },
                    }}
                  >
                    My Profile
                  </MenuItem>
                )}
                <MenuItem
                  onClick={handleSignOut}
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 300,
                    letterSpacing: "0.05em",
                    color: "#333333",
                    transition: "opacity 0.2s",
                    "&:hover": {
                      opacity: 0.7,
                      background: "rgba(51, 51, 51, 0.04)",
                    },
                  }}
                >
                  Sign Out
                </MenuItem>
              </Menu>
            </Box>

            {/* Desktop Menu */}
            <Box className="hidden md:block">
              <IconButton
                size="small"
                aria-label="account"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                className="text-[#333333] transition-opacity hover:opacity-70"
                sx={{
                  border: "1px solid rgba(51, 51, 51, 0.2)",
                  borderRadius: "0.375rem",
                  padding: "0.375rem",
                  "&:hover": {
                    border: "1px solid rgba(51, 51, 51, 0.6)",
                    background: "transparent",
                  },
                }}
              >
                <AccountCircleIcon fontSize="small" />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                open={open}
                onClose={handleClose}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    border: "1px solid rgba(51, 51, 51, 0.2)",
                    borderRadius: "0.375rem",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)",
                  },
                }}
              >
                {hasProfile && (
                  <MenuItem
                    onClick={() => handleNavigation("/profile")}
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 300,
                      letterSpacing: "0.05em",
                      color: "#333333",
                      transition: "opacity 0.2s",
                      "&:hover": {
                        opacity: 0.7,
                        background: "rgba(51, 51, 51, 0.04)",
                      },
                    }}
                  >
                    My Profile
                  </MenuItem>
                )}
                <MenuItem
                  onClick={handleSignOut}
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 300,
                    letterSpacing: "0.05em",
                    color: "#333333",
                    transition: "opacity 0.2s",
                    "&:hover": {
                      opacity: 0.7,
                      background: "rgba(51, 51, 51, 0.04)",
                    },
                  }}
                >
                  Sign Out
                </MenuItem>
              </Menu>
            </Box>
          </>
        )}
      </div>
    </header>
  );
};

export default AppHeader;
